package com.havn.app.core.qr

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.HavnGold
import com.havn.app.core.designsystem.HavnSurfaceElevated

@Composable
fun HavnQrCode(
    payload: String,
    modifier: Modifier = Modifier,
    darkColor: Color = Color(0xFF05080D),
    lightColor: Color = HavnGold
) {
    val matrix = remember(payload) { runCatching { HavnQrMatrix.encode(payload) }.getOrNull() }
    Box(
        modifier = modifier
            .aspectRatio(1f)
            .background(HavnSurfaceElevated)
            .padding(10.dp)
    ) {
        if (matrix == null) {
            Text(
                text = "QR",
                modifier = Modifier.align(Alignment.Center),
                color = lightColor,
                style = MaterialTheme.typography.labelLarge
            )
            return@Box
        }
        Canvas(Modifier.aspectRatio(1f)) {
            val quietZone = 4
            val totalModules = matrix.size + quietZone * 2
            val moduleSize = size.minDimension / totalModules.toFloat()
            drawRect(lightColor, size = Size(moduleSize * totalModules, moduleSize * totalModules))
            matrix.forEachIndexed { row, values ->
                values.forEachIndexed { column, active ->
                    if (active) {
                        drawRect(
                            color = darkColor,
                            topLeft = Offset(
                                x = (column + quietZone) * moduleSize,
                                y = (row + quietZone) * moduleSize
                            ),
                            size = Size(moduleSize, moduleSize)
                        )
                    }
                }
            }
        }
    }
}

object HavnQrMatrix {
    private const val Version = 5
    private const val Size = Version * 4 + 17
    private const val DataCodewords = 108
    private const val ErrorCorrectionCodewords = 26
    private const val MaskPattern = 0

    fun encode(payload: String): List<List<Boolean>> {
        val data = encodeData(payload)
        val errorCorrection = reedSolomon(data, ErrorCorrectionCodewords)
        val codewords = data + errorCorrection
        val modules = Array(Size) { BooleanArray(Size) }
        val reserved = Array(Size) { BooleanArray(Size) }

        drawFunctionPatterns(modules, reserved)
        drawData(modules, reserved, codewords)
        drawFormatBits(modules, reserved)

        return modules.map { row -> row.toList() }
    }

    private fun encodeData(payload: String): List<Int> {
        val bytes = payload.encodeToByteArray()
        require(bytes.size <= 106) { "HAVN QR payload is too large." }
        val bits = mutableListOf<Int>()
        appendBits(bits, 0b0100, 4)
        appendBits(bits, bytes.size, 8)
        bytes.forEach { appendBits(bits, it.toInt() and 0xFF, 8) }
        repeat(minOf(4, DataCodewords * 8 - bits.size)) { bits += 0 }
        while (bits.size % 8 != 0) bits += 0

        val codewords = bits.chunked(8).map { byteBits ->
            byteBits.fold(0) { acc, bit -> (acc shl 1) or bit }
        }.toMutableList()
        var pad = 0
        while (codewords.size < DataCodewords) {
            codewords += if (pad % 2 == 0) 0xEC else 0x11
            pad += 1
        }
        return codewords
    }

    private fun drawFunctionPatterns(modules: Array<BooleanArray>, reserved: Array<BooleanArray>) {
        drawFinder(modules, reserved, 0, 0)
        drawFinder(modules, reserved, Size - 7, 0)
        drawFinder(modules, reserved, 0, Size - 7)
        drawAlignment(modules, reserved, 30, 30)

        for (i in 0 until Size) {
            if (!reserved[6][i]) setFunction(modules, reserved, i, 6, i % 2 == 0)
            if (!reserved[i][6]) setFunction(modules, reserved, 6, i, i % 2 == 0)
        }

        for (i in 0..8) {
            if (i != 6) {
                reserve(reserved, 8, i)
                reserve(reserved, i, 8)
            }
        }
        for (i in 0..7) {
            reserve(reserved, Size - 1 - i, 8)
            reserve(reserved, 8, Size - 1 - i)
        }
        setFunction(modules, reserved, 8, Version * 4 + 9, true)
    }

    private fun drawFinder(modules: Array<BooleanArray>, reserved: Array<BooleanArray>, x: Int, y: Int) {
        for (dy in -1..7) {
            for (dx in -1..7) {
                val xx = x + dx
                val yy = y + dy
                if (xx !in 0 until Size || yy !in 0 until Size) continue
                val active = dx in 0..6 && dy in 0..6 &&
                    (dx == 0 || dx == 6 || dy == 0 || dy == 6 || (dx in 2..4 && dy in 2..4))
                setFunction(modules, reserved, xx, yy, active)
            }
        }
    }

    private fun drawAlignment(modules: Array<BooleanArray>, reserved: Array<BooleanArray>, centerX: Int, centerY: Int) {
        for (dy in -2..2) {
            for (dx in -2..2) {
                val active = maxOf(kotlin.math.abs(dx), kotlin.math.abs(dy)) != 1
                setFunction(modules, reserved, centerX + dx, centerY + dy, active)
            }
        }
    }

    private fun drawData(modules: Array<BooleanArray>, reserved: Array<BooleanArray>, codewords: List<Int>) {
        val bits = codewords.flatMap { codeword -> (7 downTo 0).map { (codeword ushr it) and 1 } }
        var bitIndex = 0
        var upward = true
        var x = Size - 1
        while (x > 0) {
            if (x == 6) x -= 1
            val rows = if (upward) Size - 1 downTo 0 else 0 until Size
            for (y in rows) {
                for (dx in 0..1) {
                    val xx = x - dx
                    if (reserved[y][xx]) continue
                    val bit = if (bitIndex < bits.size) bits[bitIndex] == 1 else false
                    val masked = if ((xx + y) % 2 == 0) !bit else bit
                    modules[y][xx] = masked
                    bitIndex += 1
                }
            }
            upward = !upward
            x -= 2
        }
    }

    private fun drawFormatBits(modules: Array<BooleanArray>, reserved: Array<BooleanArray>) {
        val bits = formatBits(errorCorrectionLevel = 1, mask = MaskPattern)
        val first = listOf(
            8 to 0, 8 to 1, 8 to 2, 8 to 3, 8 to 4, 8 to 5, 8 to 7, 8 to 8,
            7 to 8, 5 to 8, 4 to 8, 3 to 8, 2 to 8, 1 to 8, 0 to 8
        )
        val second = listOf(
            Size - 1 to 8, Size - 2 to 8, Size - 3 to 8, Size - 4 to 8, Size - 5 to 8, Size - 6 to 8, Size - 7 to 8,
            8 to Size - 8, 8 to Size - 7, 8 to Size - 6, 8 to Size - 5, 8 to Size - 4, 8 to Size - 3, 8 to Size - 2, 8 to Size - 1
        )
        first.forEachIndexed { index, (x, y) -> setFunction(modules, reserved, x, y, ((bits ushr index) and 1) == 1) }
        second.forEachIndexed { index, (x, y) -> setFunction(modules, reserved, x, y, ((bits ushr index) and 1) == 1) }
    }

    private fun formatBits(errorCorrectionLevel: Int, mask: Int): Int {
        var data = (errorCorrectionLevel shl 3) or mask
        var rem = data shl 10
        val generator = 0b10100110111
        for (i in 14 downTo 10) {
            if (((rem ushr i) and 1) != 0) rem = rem xor (generator shl (i - 10))
        }
        return (((data shl 10) or rem) xor 0b101010000010010) and 0x7FFF
    }

    private fun reedSolomon(data: List<Int>, degree: Int): List<Int> {
        val generator = rsGenerator(degree)
        val result = IntArray(degree)
        data.forEach { value ->
            val factor = value xor result[0]
            for (i in 0 until degree - 1) result[i] = result[i + 1]
            result[degree - 1] = 0
            generator.forEachIndexed { index, coefficient ->
                result[index] = result[index] xor gfMultiply(coefficient, factor)
            }
        }
        return result.toList()
    }

    private fun rsGenerator(degree: Int): List<Int> {
        var result = listOf(1)
        repeat(degree) { index ->
            val next = MutableList(result.size + 1) { 0 }
            result.forEachIndexed { coefficientIndex, coefficient ->
                next[coefficientIndex] = next[coefficientIndex] xor gfMultiply(coefficient, 1)
                next[coefficientIndex + 1] = next[coefficientIndex + 1] xor gfMultiply(coefficient, gfPow(2, index))
            }
            result = next
        }
        return result.drop(1)
    }

    private fun gfPow(value: Int, exponent: Int): Int {
        var result = 1
        repeat(exponent) { result = gfMultiply(result, value) }
        return result
    }

    private fun gfMultiply(a: Int, b: Int): Int {
        var x = a
        var y = b
        var result = 0
        while (y != 0) {
            if ((y and 1) != 0) result = result xor x
            x = x shl 1
            if ((x and 0x100) != 0) x = x xor 0x11D
            y = y ushr 1
        }
        return result and 0xFF
    }

    private fun appendBits(target: MutableList<Int>, value: Int, count: Int) {
        for (i in count - 1 downTo 0) target += (value ushr i) and 1
    }

    private fun setFunction(modules: Array<BooleanArray>, reserved: Array<BooleanArray>, x: Int, y: Int, active: Boolean) {
        modules[y][x] = active
        reserve(reserved, x, y)
    }

    private fun reserve(reserved: Array<BooleanArray>, x: Int, y: Int) {
        if (x in 0 until Size && y in 0 until Size) reserved[y][x] = true
    }
}
